import { type Courses } from "@common/components/Courses/types";
import Button, { type ButtonProps } from "@common/controls/Button/component";
import CheckboxGroup from "@common/controls/CheckboxGroup/component";
import { RadioButtonGroup, type RadioOptionItem } from "@common/controls/Inputs/Radio";
import { type ModalProps } from "@common/controls/Modal/component";
import ConfirmModal from "@common/controls/Modal/Modals/Confirm/component";
import { IconType, type OptionItem } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import React from "react";

type QuestionProps = {
  answerIds: string[];
  answerOptions: RadioOptionItem[];
  isBusy: boolean;
  isLast: boolean;
  isMultipleAnswers: boolean;
  isReadonly: boolean;

  question: string;
  onChange?: (answerIds: string[]) => void;
  answer: () => void;
  back?: () => void;

  ButtonComponent?: React.ComponentType<ButtonProps>;
};
export const Question = ({
  answer,
  answerIds,
  answerOptions,
  back,
  isBusy,
  isLast,
  isMultipleAnswers,
  isReadonly,
  question,
  ...props
}: QuestionProps) => {
  const i18n = useI18n();

  const onChange = React.useCallback(
    (value: string | null | string[]) => {
      props.onChange && props.onChange(Array.isArray(value) ? value : [value!]);
    },
    [props.onChange]
  );
  const ButtonComponent = props.ButtonComponent ?? Button;
  return (
    <>
      <div className="question">
        <div className="header">{question}</div>

        {isMultipleAnswers ? (
          <CheckboxGroup isDisabled={isReadonly} options={answerOptions} onChange={onChange} value={answerIds} />
        ) : (
          <RadioButtonGroup
            options={answerOptions}
            orientation="vertical"
            onChange={onChange}
            isDisabled={isReadonly}
            value={answerIds[0]}
          />
        )}
      </div>

      <div className="navigation">
        {back && (
          <ButtonComponent className="btn-light" isDisabled={isBusy} icon={IconType.ArrowLeft} onClick={back}>
            {i18n.previousQuestion}
          </ButtonComponent>
        )}
        <div className="gap" />
        <ButtonComponent
          className="btn-primary"
          isDisabled={isBusy || (!isMultipleAnswers && !answerIds.length)}
          icon={IconType.ArrowRight}
          onClick={answer}
        >
          {isLast ? i18n.checkResults : i18n.nextQuestion}
        </ButtonComponent>
      </div>
    </>
  );
};

type NewQuestionProps = {
  answer: (questionId: string, answerIds: string[]) => void;
  back: () => void;

  isBusy: boolean;
  state: Courses.QuizState;

  ConfirmModalComponent?: React.ComponentType<ModalProps>;
};
export const NewQuestion = (props: NewQuestionProps) => {
  const i18n = useI18n();

  const question = props.state.question!;

  const [answerIds, setAnswerIds] = React.useState<string[]>(question.answerIds ?? []);

  // NOTE: put question.id into the deps, because we need to reset answers when the question changes
  React.useEffect(() => setAnswerIds(question.answerIds ?? []), [question.answerIds, question.id]);

  const options = React.useMemo(
    (): OptionItem<string>[] =>
      question.answers
        .sort((a, b) => a.answer.localeCompare(b.answer))
        .map((x) => ({
          label: x.answer,
          value: x.id
        })),
    [question.answers]
  );

  const [isConfirming, setIsConfirming] = React.useState(false);
  const onConfirmClose = React.useCallback(() => setIsConfirming(false), [setIsConfirming]);

  const isLast = React.useMemo(() => props.state.questionCount - 1 === props.state.question!.index, [props.state]);

  const answer = React.useCallback(() => {
    if (!isConfirming && isLast) {
      setIsConfirming(true);
    } else {
      setIsConfirming(false);
      props.answer(question.id, answerIds);
    }
  }, [question.id, answerIds, props.answer, isConfirming, isLast]);

  const ConfirmModalComponent = props.ConfirmModalComponent ?? ConfirmModal;

  return (
    <>
      <Question
        answer={answer}
        answerIds={answerIds}
        answerOptions={options}
        back={
          props.state.seenQuestions.length && props.state.seenQuestions.findIndex((x) => x.id === question.id) !== 0
            ? props.back
            : undefined
        }
        onChange={setAnswerIds}
        isBusy={props.isBusy}
        isLast={isLast}
        isMultipleAnswers={question.isMultipleAnswers}
        isReadonly={false}
        question={question.question}
      />

      <ConfirmModalComponent
        onClose={onConfirmClose}
        isVisible={isConfirming}
        title={i18n.submitAnswers}
        onConfirm={answer}
      >
        {i18n.infoConfirmationSubmitAnswers}
      </ConfirmModalComponent>
    </>
  );
};
